/**
 * usePaypalDonationCheckout — wrapper réutilisable du checkout PayPal
 * Connected Path Multiparty pour les pages de don de donoor.org.
 *
 * Encapsule :
 *   - L'instanciation de `useCheckout` avec provider="paypal"
 *   - Les fonctions `createOrder` / `createSubscription` qui appellent
 *     les bons endpoints backend Donoor (/api/public/paypal/*)
 *   - Le routage automatique selon recurring/one-time
 *   - Les computeds `isPaypalOnlyMode` et `showPayPalAlternative`
 *
 * Utilisation depuis une page de don :
 *   ```ts
 *   const paypal = usePaypalDonationCheckout({
 *     paymentProviders: donationData.paymentProviders,
 *     paymentForm,
 *     calculations,
 *     donateUs: donationData.donateUs,
 *     donateToUsAmount,
 *     organizationId: donationData.organizationId,
 *     contextIds: { qr_code_id, campaign_id, ticket_order_id },
 *     successUrl: (email) => `/donation-success?qrCodeId=${id}${email ? `&email=${email}` : ""}`,
 *   });
 *   // → paypal.checkout (useCheckout return), paypal.isPaypalOnlyMode,
 *   //   paypal.showPayPalAlternative, paypal.errorMessage, paypal.initError
 *   ```
 *
 * Cf. memory/paypal_integration_plan.md §7.1
 */

import { computed, type Ref, type ComputedRef } from "vue";
import { useCheckout } from "@donoor/checkout";
import type { OrgPaymentProviders } from "./useOrganizationPaymentInfo";
import { generateIdempotencyKey } from "~/utils/idempotencyKey";
import { withDonationAmountParams } from "~/utils/donationSuccessUrl";
import { paypalDonationApi } from "~/services/api/donationCheckout";

/**
 * Alias rétrocompatible — l'ancien nom était `PaymentProvidersInfo`. Le
 * type vit maintenant dans useOrganizationPaymentInfo (source de vérité).
 */
export type PaymentProvidersInfo = OrgPaymentProviders;

/**
 * Vue minimale du composable `usePaymentForm` consommée par PayPal :
 * uniquement les refs réellement lues ici. On évite `paymentForm: any`
 * sans coupler le composable à toute la surface de `usePaymentForm`.
 */
interface PaymentFormSlice {
  form: Ref<{
    donorEmail: string;
    donorName: string;
    /** Message libre du donateur (cagnotte/QR) → transactions.donor_message. */
    message: string;
    isAnonymous: boolean;
  }>;
  includeFees: Ref<boolean>;
  isSubscription: Ref<boolean> | ComputedRef<boolean>;
  subscriptionPeriod: Ref<string>;
}

/**
 * Vue minimale du composable `usePaymentCalculations` consommée par PayPal.
 */
interface PaymentCalculationsSlice {
  finalAmount: Ref<number> | ComputedRef<number>;
  amountWithFees: Ref<number> | ComputedRef<number>;
}

export interface UsePaypalDonationCheckoutOptions {
  /** Reactive des providers actifs sur l'org (issu de useDonationData). */
  paymentProviders:
    | Ref<PaymentProvidersInfo>
    | ComputedRef<PaymentProvidersInfo>;
  /** Composable de form Donoor (`usePaymentForm()`). */
  paymentForm: PaymentFormSlice;
  /** Calculs dérivés (`usePaymentCalculations()`). */
  calculations: PaymentCalculationsSlice;
  /** Reactive du flag "Soutenir Donoor" (depuis useDonationData). */
  donateUs?: Ref<boolean> | ComputedRef<boolean>;
  /** Montant additionnel "Soutenir Donoor" si donateUs activé. */
  donateToUsAmount?: Ref<number> | ComputedRef<number>;
  /** Reactive de l'organizationId (issu de useDonationData). */
  organizationId: Ref<string | null> | ComputedRef<string | null>;
  /**
   * IDs métier optionnels passés en body au backend (le backend les utilise
   * pour relier la transaction au QR code / campagne / commande billet /
   * adhésion via `custom_id` PayPal).
   */
  contextIds?: {
    qr_code_id?: string;
    campaign_id?: number | string;
    ticket_order_id?: string;
    membership_order_id?: string;
  };
  /** Construit l'URL de redirection après succès (donation-success). */
  successUrl: (donorEmail?: string) => string;  /** Couleur de l'association, transmise à la page de remerciement (« #RRGGBB »). */
  couleur?: () => string | null | undefined;
}

export function usePaypalDonationCheckout(
  opts: UsePaypalDonationCheckoutOptions,
) {
  const config = useRuntimeConfig();
  // Langue de la page : champs carte et messages Stripe/PayPal traduits.
  const { localeIntl } = useFormatLangue();
  const { t } = useI18n();

  // Computeds publics : indiquent à la page quel mode UI afficher
  const isPaypalOnlyMode = computed(
    () =>
      !opts.paymentProviders.value.stripe.charges_enabled &&
      opts.paymentProviders.value.paypal.charges_enabled,
  );

  const showPayPalAlternative = computed(
    () =>
      opts.paymentProviders.value.stripe.charges_enabled &&
      opts.paymentProviders.value.paypal.charges_enabled,
  );

  // Builders pour les requêtes backend.
  // createOrder gère le cas one-time, createSubscription le cas récurrent.
  // Le PaypalProvider du package @donoor/checkout appelle l'une ou l'autre
  // via le `createPaymentIntent` qu'on passe à useCheckout, selon
  // l'état du `recurring` ref.

  async function createPaypalOrder(
    ctx: import("@donoor/checkout").CreatePaymentIntentContext,
  ): Promise<import("@donoor/checkout").PaymentIntentResult> {
    // ⚠️ Le donate_to_us doit TOUJOURS être ajouté au montant chargé, même si
    // l'utilisateur n'a pas coché « inclure les frais ». Sans ça, le donateur
    // sélectionnait 10€ + 2€ pourboire mais ne payait que 10€ au final.
    // Karim 2026-06-28.
    const donateToUs = opts.donateToUsAmount?.value || 0;
    const amountToCharge = opts.paymentForm.includeFees.value
      ? opts.calculations.amountWithFees.value
      : opts.calculations.finalAmount.value + donateToUs;

    // Don récurrent (Vault) : le 1er cycle est un Order vaulting. On envoie
    // recurring + interval pour que le backend stocke le moyen de paiement et
    // arme la subscription à la capture. Le pourboire est inclus dans
    // `amountToCharge` → prélevé À CHAQUE cycle (parité Stripe/HelloAsso).
    const isRecurring = ctx.recurring === true;
    const recurringInterval = ({
      weekly: "week",
      monthly: "month",
      yearly: "year",
    } as const)[opts.paymentForm.subscriptionPeriod.value];

    const body: Record<string, unknown> = {
      organization_id: opts.organizationId.value,
      amount: Math.round(amountToCharge * 100),
      net_amount: Math.round(opts.calculations.finalAmount.value * 100),
      currency: "EUR",
      donation_type: isRecurring ? "recurring" : "one_time",
      donor_email: ctx.donor.email || undefined,
      donor_name: !opts.paymentForm.form.value.isAnonymous
        ? ctx.donor.name || opts.paymentForm.form.value.donorName
        : undefined,
      include_fees: opts.paymentForm.includeFees.value,
      ...(isRecurring
        ? { recurring: true, recurring_interval: recurringInterval }
        : {}),
      ...opts.contextIds,
    };
    if (
      opts.donateUs?.value &&
      opts.donateToUsAmount?.value &&
      opts.donateToUsAmount.value > 0
    ) {
      body.donate_to_us = Math.round(opts.donateToUsAmount.value * 100);
    }

    const response = await paypalDonationApi.createOrder(body, generateIdempotencyKey());

    if (!response?.success || !response.data?.order_id) {
      throw new Error(response?.message || t("don.checkoutErreurs.echecCreationOrderPaypal"));
    }

    return {
      clientSecret: response.data.order_id,
      transactionId: response.data.order_id,
    };
  }

  /**
   * Donateur saisi sur la page, transmis à la capture. Un paiement par CARTE
   * via PayPal n'a pas de compte PayPal derrière : PayPal ne renvoie ni nom
   * ni e-mail, le don naissait anonyme et sans reçu possible (Team Elite
   * Pétanque, cagnotte, Karim 2026-10-22). Même voie que le message.
   */
  function donateurSaisi() {
    const f = opts.paymentForm.form.value;
    return {
      donor_email: f.donorEmail?.trim() || null,
      donor_name: f.isAnonymous ? null : f.donorName?.trim() || null,
      is_anonymous: f.isAnonymous === true,
    };
  }

  /**
   * Capture serveur-side d'un order PayPal après approval par le donateur.
   * REQUIS pour finaliser le paiement : sans cet appel, l'order reste en
   * `APPROVED` et n'est jamais converti en transaction réelle.
   *
   * Renvoie l'email + le name du payer extraits de la response PayPal —
   * utile quand le donateur n'a pas saisi ses infos avant de cliquer le
   * bouton PayPal (pattern analogue à Stripe wallet email).
   */
  async function capturePaypalOrder(orderId: string): Promise<{
    payerEmail?: string;
    payerName?: string;
  }> {
    // Sandbox-only : ?paypal_mock_decline=1 dans l'URL force le backend à
    // injecter PayPal-Mock-Response: INSTRUMENT_DECLINED → simule un paiement
    // refusé pour le scenario « declined payment » de l'IWT PayPal vidéo 4.
    // Karim 2026-06-28.
    const mockDecline =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("paypal_mock_decline") === "1";

    // Don récurrent (Vault) : on transmet à la capture la décomposition figée
    // (net / pourboire / include_fees / interval) pour que le backend crée la
    // subscription avec le token vaulté après capture du 1er cycle. Ces valeurs
    // ne changent pas le montant capturé (fixé à la création de l'Order) : elles
    // pilotent le recalcul commission/pourboire des cycles suivants.
    const isRecurring = opts.paymentForm.isSubscription.value;
    const donateToUs = opts.donateToUsAmount?.value || 0;
    const recurringParams = isRecurring
      ? {
          recurring: true,
          recurring_interval: ({
            weekly: "week",
            monthly: "month",
            yearly: "year",
          } as const)[opts.paymentForm.subscriptionPeriod.value],
          net_amount: Math.round(opts.calculations.finalAmount.value * 100),
          donate_to_us: donateToUs > 0 ? Math.round(donateToUs * 100) : 0,
          include_fees: opts.paymentForm.includeFees.value,
          qr_code_id:
            (opts.contextIds as { qr_code_id?: string }).qr_code_id ?? null,
        }
      : {};

    const response = await paypalDonationApi.captureOrder(orderId, {
      organization_id: opts.organizationId.value,
      // Message libre du donateur — le webhook PayPal ne le porte pas, on le
      // transmet à la capture synchrone pour qu'il atterrisse dans
      // transactions.donor_message (parité Stripe). Karim 2026-06-24.
      message: opts.paymentForm.form.value.message || null,
      ...donateurSaisi(),
      ...recurringParams,
      ...(mockDecline ? { paypal_mock_decline: true } : {}),
    });

    if (!response?.success || !response.data) {
      throw new Error(response?.message || t("don.checkoutErreurs.echecCapturePaypal"));
    }

    return {
      payerEmail: response.data.payer_email ?? undefined,
      payerName: response.data.payer_name ?? undefined,
    };
  }

  // URL de succès enrichie des montants du don → la page /donation-success
  // affiche la confirmation instantanément (sans attendre le webhook).
  // Cf. ~/utils/donationSuccessUrl.ts pour le pourquoi complet.
  const successUrlWithAmounts = withDonationAmountParams(opts.successUrl, {
    couleur: opts.couleur,
    includeFees: opts.paymentForm.includeFees,
    finalAmount: opts.calculations.finalAmount,
    amountWithFees: opts.calculations.amountWithFees,
    donateToUsAmount: opts.donateToUsAmount,
    isSubscription: opts.paymentForm.isSubscription,
    // Mapping période → interval Stripe-like, lu par useDonationSuccess
    // (`route.query.interval`) pour l'affichage « par semaine/mois/an ».
    interval: computed(
      () =>
        ({ weekly: "week", monthly: "month", yearly: "year" })[
          opts.paymentForm.subscriptionPeriod.value
        ] ?? "month",
    ),
  });

  // Instance useCheckout. Le SDK PayPal n'est chargé qu'au premier
  // mountExpressCheckout, donc pas de surcoût si la page n'utilise pas
  // PayPal in fine.
  const checkout = useCheckout({
    provider: "paypal",
    locale: localeIntl.value,
    publicKey: (config.public.paypalClientId as string) || "",
    // Getter (PAS valeur figée) : le merchant_id PayPal est chargé de façon
    // asynchrone avec l'org. Résolu à l'init du provider.
    connectedAccountId: () =>
      opts.paymentProviders.value.paypal.merchant_id || undefined,
    currency: "eur",
    // BN code REQUIS pour l'Integration Walkthrough Checklist PayPal.
    // Cf. memory/paypal_official_integration_specs.md.
    partnerAttributionId:
      (config.public.paypalPartnerAttributionId as string) || "DONOOR_SP_PPCP",
    pageType: "checkout",
    // Sandbox UNIQUEMENT hors prod : en production PayPal refuse `buyer-country`
    // (400 → SDK cassé). mode="prod" en live, "dev" en local.
    sandbox: (config.public.mode as string) !== "prod",
    amount: computed(() => {
      const donateToUs = opts.donateToUsAmount?.value || 0;
      return opts.paymentForm.includeFees.value
        ? opts.calculations.amountWithFees.value
        : opts.calculations.finalAmount.value + donateToUs;
    }),
    donor: computed(() => ({
      email: opts.paymentForm.form.value.donorEmail,
      name: !opts.paymentForm.form.value.isAnonymous
        ? opts.paymentForm.form.value.donorName
        : undefined,
    })),
    recurring: opts.paymentForm.isSubscription,
    feesCovered: opts.paymentForm.includeFees,
    // Récurrent ET one-time passent par le flux Order (le récurrent en mode
    // vaulting). L'ancien flux Subscriptions API (createPaypalSubscription) est
    // remplacé par Vault + Orders programmés (ne route pas de platform_fees).
    createPaymentIntent: async (ctx) => createPaypalOrder(ctx),
    successUrl: successUrlWithAmounts,
    onCapture: capturePaypalOrder,
  });

  const initError = computed(() => checkout.error.value !== null);
  const errorMessage = computed(() => checkout.error.value?.message || "");

  return {
    /** useCheckout instance (mountExpressCheckout, destroy, status…) */
    checkout,
    /** Mode A : asso n'a connecté QUE PayPal (pas Stripe). */
    isPaypalOnlyMode,
    /** Mode B : asso a Stripe + PayPal connectés (PayPal en alternative). */
    showPayPalAlternative,
    /** Computed boolean : true si une erreur d'init du SDK PayPal */
    initError,
    /** Computed string : message d'erreur (vide si pas d'erreur) */
    errorMessage,
  };
}
